import { DimeDetailPageStates } from '@nudool/design-system';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useBreadcrumbLabel } from '../../components/BreadcrumbLabelsContext.js';
import { assertUuidRouteParams } from '../../lib/assert-uuid-route-params.js';
import { RouteGuard } from '../../lib/route-guard.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import { salesOrderLinesRead } from '../../modules/sales/sales-order-action-config/index.js';
import { salesOrderDetailMoveBackTarget } from '../../modules/sales/sales-order-detail-action-layout.js';
import { SalesOrderDetailLinesCard } from '../../modules/sales/sales-order-detail-lines-card.js';
import { SalesOrderDetailPaymentsCard } from '../../modules/sales/sales-order-detail-payments-card.js';
import { SalesOrderDetailSkeleton } from '../../modules/sales/sales-order-detail-skeleton.js';
import { SalesOrderDetail } from '../../modules/sales/sales-order-detail.js';
import {
  SalesOrderEditSheet,
  type EditSheetSectionId,
} from '../../modules/sales/sales-order-edit-sheet.js';
import { salesOrderEditWarehouseLock } from '../../modules/sales/sales-order-edit-warehouse-lock.js';
import { SalesOrderHistoryTab } from '../../modules/sales/sales-order-history-tab.js';
import { SalesOrderLeafLinkPanel } from '../../modules/sales/sales-order-leaflink-panel.js';
import { salesOrderPickAccess } from '../../modules/sales/sales-order-pick-access.js';
import { useSalesOrderDetailPageData } from '../../modules/sales/use-sales-order-detail-page-data.js';
import { useSalesOrderLinePickExtras } from '../../modules/sales/use-sales-order-line-pick-extras.js';

// The raw GraphQL error is never shown; it can carry server internals.
const LOAD_ERROR_MESSAGE = 'Something went wrong loading this order.';

export const Route = createFileRoute('/sales-orders/$salesOrderId')({
  beforeLoad: ({ params }) => assertUuidRouteParams(params),
  component: SalesOrderDetailPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function SalesOrderDetailPage() {
  const { salesOrderId } = Route.useParams();
  const { permissions } = usePermissions();
  // N30: payments use the payment module's permissions, not salesOrder:payment.
  const canViewPayments = permissions.includes('payment:view');
  const canViewPicks = salesOrderPickAccess(permissions).canRead;
  const canViewLeafLink = permissions.includes('leafLinkIntegration:view');
  const canRevert = permissions.includes('salesOrder:revertStatus');

  const {
    salesOrder,
    isDraft,
    lines,
    loading,
    loadError,
    linesLoading,
    linesRefreshing,
    linesFailed,
    lineTypeIds,
    lineTypesFailed,
    paymentNodes,
    paymentsLoading,
    paymentsError,
    workflowStateData,
    workflowStateLoading,
    workflowStateFailed,
    pickId,
    pickFacts,
    nextStepFacts,
    history,
    refetchAll,
  } = useSalesOrderDetailPageData({ salesOrderId, canViewPayments, canViewPicks });
  const pickExtras = useSalesOrderLinePickExtras({ pickFacts });
  useBreadcrumbLabel(salesOrderId, salesOrder?.num);
  // One value for both, so a decision never hashes lines the page is still re-reading.
  const linesRead = salesOrderLinesRead({
    loading: linesLoading,
    refreshing: linesRefreshing,
    failed: linesFailed,
  });
  // The order says whether picking started (no picking permission needed); the pick read only names it.
  const warehouseLock = salesOrderEditWarehouseLock(salesOrder?.pickStarted === true, pickFacts);
  const [edit, setEdit] = useState<{ open: boolean; section?: EditSheetSectionId }>({
    open: false,
  });
  // ML-UX-2: while Edit Lines is open the page's actions and section pencils wait for it; tied to its order.
  const [linesEditingOrderId, setLinesEditingOrderId] = useState<string | null>(null);
  const linesEditing = linesEditingOrderId === salesOrderId;

  // A close can follow a partial save (campaigns refused), so the page always re-reads the order.
  function closeEdit() {
    setEdit((current) => ({ ...current, open: false }));
    void refetchAll();
  }

  // N29: a Draft lives in the Sales Orders list (booking workspace) until it's submitted.
  if (isDraft) return <Navigate to="/sales-orders" replace />;

  return (
    <RouteGuard route="/sales-orders/$salesOrderId">
      {/* R1.9: no back-to-list link, because the side nav and the browser's back already are the route back. */}
      <div className="flex flex-col gap-4">
        {/* With errorPolicy 'all' `loadError` can sit beside a usable order, so it is fatal only when there is no order (§11). */}
        <DimeDetailPageStates
          data={salesOrder}
          loading={loading}
          error={loadError ? { message: LOAD_ERROR_MESSAGE } : null}
          entityLabel="Sales order"
          onRetry={() => void refetchAll()}
          skeleton={<SalesOrderDetailSkeleton />}
        >
          {(order) => (
            <>
              <SalesOrderDetail
                order={order}
                onChanged={refetchAll}
                lines={linesRead === 'ready' ? lines : null}
                linesRead={linesRead}
                linesEditing={linesEditing}
                lineTypeIds={lineTypeIds}
                lineTypesFailed={lineTypesFailed}
                workflowStateData={workflowStateData}
                workflowStateLoading={workflowStateLoading}
                workflowStateFailed={workflowStateFailed}
                nextStepFacts={nextStepFacts}
                onEditOrder={() => setEdit({ open: true })}
                onEditSection={
                  linesEditing ? undefined : (section) => setEdit({ open: true, section })
                }
                leafLinkPanel={
                  canViewLeafLink ? <SalesOrderLeafLinkPanel salesOrderId={order.id} /> : null
                }
                linesTab={
                  <SalesOrderDetailLinesCard
                    key={order.id}
                    order={order}
                    lines={lines}
                    loading={linesLoading}
                    refreshing={linesRefreshing}
                    failed={linesFailed}
                    pickId={pickId}
                    pickExtras={pickExtras}
                    moveBackTarget={
                      canRevert
                        ? salesOrderDetailMoveBackTarget({
                            order,
                            lineTypeIds,
                            lineTypesFailed,
                            workflowStateData,
                            workflowStateLoading,
                            workflowStateFailed,
                          })
                        : null
                    }
                    onChanged={refetchAll}
                    onEditingChange={(editing) => setLinesEditingOrderId(editing ? order.id : null)}
                  />
                }
                historyTab={
                  <SalesOrderHistoryTab
                    entries={history.entries}
                    loading={history.loading}
                    failed={history.failed}
                    onRetry={history.retry}
                  />
                }
                paymentsCard={
                  canViewPayments ? (
                    <SalesOrderDetailPaymentsCard
                      order={order}
                      payments={paymentNodes}
                      loading={paymentsLoading}
                      failed={Boolean(paymentsError)}
                      onChanged={refetchAll}
                    />
                  ) : null
                }
              />
              <SalesOrderEditSheet
                order={{
                  ...order,
                  lines: linesLoading ? null : lines,
                  deliveryScheduled: workflowStateData?.salesOrderWorkflowState?.deliveryScheduled,
                  warehouseLock,
                }}
                linesFailed={linesFailed}
                open={edit.open}
                initialSection={edit.section}
                onOpenChange={(open) => (open ? setEdit({ open: true }) : closeEdit())}
                onSaved={closeEdit}
                onRefresh={() => void refetchAll()}
              />
            </>
          )}
        </DimeDetailPageStates>
      </div>
    </RouteGuard>
  );
}
